import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { MUSCLE_LABELS } from "../components/BodyDiagram";
import { AreasWorkedCards } from "../components/AreasWorkedCards";
import AuthImage from "../components/AuthImage";
import { ArrowLeft, Calendar, TrendingDown, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FOCUS = [];

export default function Compare() {
  const { id: clientId } = useParams();
  const [client, setClient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [leftId, setLeftId] = useState(null);
  const [rightId, setRightId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/clients/${clientId}`),
      api.get(`/sessions/by-client/${clientId}`),
    ]).then(([c, s]) => {
      setClient(c.data);
      setSessions(s.data);
      // Default to oldest vs newest
      if (s.data.length >= 2) {
        setLeftId(s.data[s.data.length - 1].id);
        setRightId(s.data[0].id);
      } else if (s.data.length === 1) {
        setLeftId(s.data[0].id);
      }
    }).catch(() => toast.error("Could not load sessions"));
  }, [clientId]);

  const left = sessions.find((s) => s.id === leftId);
  const right = sessions.find((s) => s.id === rightId);
  const fmt = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  // Memoise derived sets/arrays so child component props are referentially stable
  const leftMuscles = useMemo(() => new Set(left?.worked_areas || []), [left]);
  const rightMuscles = useMemo(() => new Set(right?.worked_areas || []), [right]);
  const sharedMuscles = useMemo(() => [...leftMuscles].filter((m) => rightMuscles.has(m)), [leftMuscles, rightMuscles]);
  const onlyLeft = useMemo(() => [...leftMuscles].filter((m) => !rightMuscles.has(m)), [leftMuscles, rightMuscles]);
  const onlyRight = useMemo(() => [...rightMuscles].filter((m) => !leftMuscles.has(m)), [leftMuscles, rightMuscles]);
  const combinedMuscles = useMemo(
    () => [...new Set([...leftMuscles, ...rightMuscles])],
    [leftMuscles, rightMuscles]
  );

  // Pain comparison
  const leftPainDrop = left?.outcome?.pain_before != null && left?.outcome?.pain_after != null
    ? left.outcome.pain_before - left.outcome.pain_after : null;
  const rightPainDrop = right?.outcome?.pain_before != null && right?.outcome?.pain_after != null
    ? right.outcome.pain_before - right.outcome.pain_after : null;

  if (!client) return <Layout><div className="text-ink-500">Loading…</div></Layout>;

  return (
    <Layout>
      <Link to={`/clients/${clientId}`} className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900 mb-6" data-testid="back-to-client">
        <ArrowLeft className="w-4 h-4" /> Back to {client.name}
      </Link>

      <div className="label-overline mb-2">Session comparison</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-2">Track progress side by side</h1>
      <p className="text-ink-700 mb-10 max-w-2xl">
        Pick two sessions to compare photos, AI analysis, worked areas, and outcomes — a powerful way to show clients tangible posture progress.
      </p>

      {sessions.length < 2 ? (
        <div className="bg-white border border-paper-200 rounded-3xl p-12 text-center">
          <div className="font-heading text-xl text-ink-900 mb-2">Need at least 2 sessions</div>
          <p className="text-ink-700 mb-6">Complete another session for this client to enable comparison.</p>
          <Link to={`/clients/${clientId}/new-session`} className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 transition-all">
            Start a session
          </Link>
        </div>
      ) : (
        <>
          {/* Session pickers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div>
              <div className="label-overline mb-2">Earlier session</div>
              <select
                data-testid="left-session-select"
                value={leftId || ""}
                onChange={(e) => setLeftId(e.target.value)}
                className="w-full min-h-[48px] px-4 rounded-xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
              >
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{fmt(s.created_at)}{s.duration_minutes ? ` · ${s.duration_minutes}min` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-overline mb-2">Later session</div>
              <select
                data-testid="right-session-select"
                value={rightId || ""}
                onChange={(e) => setRightId(e.target.value)}
                className="w-full min-h-[48px] px-4 rounded-xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
              >
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{fmt(s.created_at)}{s.duration_minutes ? ` · ${s.duration_minutes}min` : ""}</option>
                ))}
              </select>
            </div>
          </div>

          {left && right && (
            <>
              {/* Photo side-by-side */}
              <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
                <div className="label-overline mb-4">Posture progress</div>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  {["front", "side", "back"].map((k) => (
                    <div key={k}>
                      <div className="label-overline text-center mb-2">{k}</div>
                      <div className="grid grid-cols-2 gap-1 sm:gap-2">
                        <div data-testid={`left-${k}`}>
                          {left[`${k}_image`] ? (
                            <AuthImage path={left[`${k}_image`]} alt={`Left ${k}`} className="w-full h-32 sm:h-56 object-cover rounded-lg border border-paper-200" />
                          ) : <div className="w-full h-32 sm:h-56 rounded-lg bg-paper-100" />}
                          <div className="text-[10px] text-center text-ink-500 mt-1 uppercase tracking-[0.1em]">{fmt(left.created_at)}</div>
                        </div>
                        <div data-testid={`right-${k}`}>
                          {right[`${k}_image`] ? (
                            <AuthImage path={right[`${k}_image`]} alt={`Right ${k}`} className="w-full h-32 sm:h-56 object-cover rounded-lg border border-paper-200" />
                          ) : <div className="w-full h-32 sm:h-56 rounded-lg bg-paper-100" />}
                          <div className="text-[10px] text-center text-ink-500 mt-1 uppercase tracking-[0.1em]">{fmt(right.created_at)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Outcome deltas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <DeltaCard
                  label="Pain (start of session)"
                  leftVal={left.outcome?.pain_before}
                  rightVal={right.outcome?.pain_before}
                  inverted
                />
                <DeltaCard
                  label="Effectiveness"
                  leftVal={left.outcome?.effectiveness}
                  rightVal={right.outcome?.effectiveness}
                  suffix="/5"
                />
                <DeltaCard
                  label="Pain drop in session"
                  leftVal={leftPainDrop}
                  rightVal={rightPainDrop}
                  prefix="↓ "
                />
              </div>

              {/* AI summary side-by-side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <SessionSummary session={left} label="Earlier" />
                <SessionSummary session={right} label="Later" />
              </div>

              {/* Muscles comparison */}
              <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
                <div className="label-overline mb-4">Worked area overlap</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <MuscleList title={`Only ${fmt(left.created_at)}`} ids={onlyLeft} color="muted" />
                  <MuscleList title="Both sessions" ids={sharedMuscles} color="sage" />
                  <MuscleList title={`Only ${fmt(right.created_at)}`} ids={onlyRight} color="terracotta" />
                </div>
              </div>

              {/* Combined diagram - showing what was treated over both sessions */}
              <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
                <div className="label-overline mb-4">Combined treatment map (both sessions)</div>
                <AreasWorkedCards workedAreas={combinedMuscles} />
              </div>
            </>
          )}
        </>
      )}
    </Layout>
  );
}

function DeltaCard({ label, leftVal, rightVal, suffix = "", prefix = "", inverted = false }) {
  const has = (v) => v !== null && v !== undefined;
  const diff = has(leftVal) && has(rightVal) ? rightVal - leftVal : null;
  // For pain: lower is better (inverted). For effectiveness/pain drop: higher is better.
  const improved = diff != null && (inverted ? diff < 0 : diff > 0);
  const same = diff === 0;
  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-5">
      <div className="label-overline mb-3">{label}</div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-heading text-ink-900">{has(leftVal) ? `${prefix}${leftVal}${suffix}` : "—"}</div>
          <div className="text-xs text-ink-500">earlier</div>
        </div>
        <div className="text-2xl text-ink-500">→</div>
        <div className="text-right">
          <div className="text-2xl font-heading text-ink-900">{has(rightVal) ? `${prefix}${rightVal}${suffix}` : "—"}</div>
          <div className="text-xs text-ink-500">later</div>
        </div>
      </div>
      {diff != null && !same && (
        <div className={`mt-3 text-xs font-medium ${improved ? "text-sage-700" : "text-terracotta-600"}`}>
          {improved ? "▲ Improved" : "▼ Regressed"} ({diff > 0 ? "+" : ""}{diff})
        </div>
      )}
    </div>
  );
}

function SessionSummary({ session, label }) {
  const fmt = (d) => new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-ink-500" />
        <div className="label-overline">{label} — {fmt(session.created_at)}</div>
      </div>
      {session.ai_analysis?.summary && (
        <p className="text-sm text-ink-900 leading-relaxed mb-3">{session.ai_analysis.summary}</p>
      )}
      {session.ai_analysis?.problem_areas?.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertCircle className="w-3.5 h-3.5 text-terracotta-500" />
            <div className="label-overline text-terracotta-600">Findings</div>
          </div>
          <ul className="space-y-1">
            {session.ai_analysis.problem_areas.slice(0, 4).map((p, i) => (
              <li key={`${session.id}-pa-${p.area || i}`} className="text-xs text-ink-700">· {p.area} ({p.severity})</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MuscleList({ title, ids, color }) {
  const colors = {
    muted: "bg-paper-100 text-ink-700",
    sage: "bg-sage-500 text-white",
    terracotta: "bg-terracotta-500 text-white",
  };
  return (
    <div>
      <div className="label-overline mb-2">{title} ({ids.length})</div>
      <div className="flex flex-wrap gap-1.5">
        {ids.length === 0 ? (
          <span className="text-xs text-ink-500">—</span>
        ) : ids.map((m) => (
          <span key={m} className={`px-2.5 py-1 rounded-full text-xs ${colors[color]}`}>{MUSCLE_LABELS[m] || m}</span>
        ))}
      </div>
    </div>
  );
}
